import { makeWorksheetApi } from '@mathflat/domain/MakeWorksheet'
import type { Worksheet } from '@mathflat/domain/Worksheet'
import { worksheetApi } from '@mathflat/domain/Worksheet'
import { useQuery } from '@tanstack/react-query'

import queryClient from '@/controller/queryClient'
import { studentController } from '@/controller/student.controller'

import { groupAssigneesByGrade } from './groupAssigneesByGrade'

export const MOBILE_STUDENT_SELECT_KEY = (worksheetId: Worksheet['id']) =>
  ['mobileStudentSelect', worksheetId] as const

// QR 진입 판정과 학생 목록이 같은 학습지를 잇달아 묻는다. 잠깐 나눠 써 같은 요청을 두 번 보내지 않는다
const SHARED_FOR_MS = 5_000

export const WORKSHEET_ASSIGNEES_KEY = (worksheetId: Worksheet['id']) =>
  ['worksheetAssignees', worksheetId] as const

/** 다른 학원·삭제된 학습지는 실패로 받는다(ignoredForDeleted: false) */
export const fetchWorksheetDetailShared = (worksheetId: Worksheet['id']) =>
  queryClient.fetchQuery({
    queryKey: ['worksheetDetail', worksheetId],
    queryFn: () => makeWorksheetApi.getWorksheetDetail(worksheetId, { ignoredForDeleted: false }),
    staleTime: SHARED_FOR_MS,
    retry: false,
  })

export const fetchWorksheetAssigneesShared = (worksheetId: Worksheet['id']) =>
  queryClient.fetchQuery({
    queryKey: WORKSHEET_ASSIGNEES_KEY(worksheetId),
    queryFn: () => worksheetApi.getWorksheetAssignees(worksheetId),
    staleTime: SHARED_FOR_MS,
    retry: false,
  })

/** 이 선생님이 볼 수 있는 재원생. 출제 모달과 같이 홈룸 권한으로 걸러 받는다 */
export const fetchStudentGroupsShared = () =>
  queryClient.fetchQuery({
    queryKey: ['studentGroupsShared'],
    queryFn: () => studentController.fetchStudentGroups(),
    staleTime: SHARED_FOR_MS,
    retry: false,
  })

const fetchStudentSelect = async (worksheetId: Worksheet['id']) => {
  const [worksheet, assignees, studentGroups] = await Promise.all([
    fetchWorksheetDetailShared(worksheetId),
    fetchWorksheetAssigneesShared(worksheetId),
    fetchStudentGroupsShared(),
  ])
  // 출제 모달·학생별 QR 과 같은 범위만 보인다. 범위 밖 학생의 QR 은 담당 학생이 아니라며 막힌다
  const students = (studentGroups.studentColl?.toArr ?? []).map(({ id, schoolGrade }) => ({
    id,
    schoolType: schoolGrade.schoolType,
    grade: schoolGrade.grade,
  }))
  const visibleIds = new Set(students.map(({ id }) => id))

  return {
    title: worksheet.title,
    groups: groupAssigneesByGrade(
      assignees.filter(({ studentId }) => visibleIds.has(studentId)),
      students,
    ),
  }
}

export const useMobileStudentSelectQuery = (worksheetId: Worksheet['id']) =>
  useQuery({
    queryKey: MOBILE_STUDENT_SELECT_KEY(worksheetId),
    queryFn: () => fetchStudentSelect(worksheetId),
  })
